// frontend/src/pages/Agreements/AgreementNew.jsx
import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { getEntities } from "../../api/entity.api.js";
import { getVendors } from "../../api/vendor.api.js";
import { getPartners } from "../../api/partner.api.js";
import {
  submitAgreement,
  uploadAgreementFile,
} from "../../api/agreement.api.js";
import { FormField, ErrorMessage, Spinner } from "../../components/ui";
import { ChevronLeft, Upload } from "lucide-react";

export default function AgreementNew() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { isMaster } = useAuth();

  const [form, setForm] = useState({
    title: "",
    scope: "",
    entityCacheId: "",
    stakeholders: "",
    startDate: "",
    endDate: "",
    paymentDeadline: "",
    objectOfAgreement: "",
  });
  // Admin-only: which counterparty this agreement is for
  const [counterpartyType, setCounterpartyType] = useState("VENDOR");
  const [counterpartyId, setCounterpartyId] = useState("");

  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const { data: entities, isLoading: loadingEntities } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
  });

  // Only fetch vendor/partner lists when admin is using this page
  const { data: vendors } = useQuery({
    queryKey: ["vendors-list"],
    queryFn: () => getVendors({ isVerified: true }),
    enabled: isMaster,
  });

  const { data: partners } = useQuery({
    queryKey: ["partners-list"],
    queryFn: () => getPartners(),
    enabled: isMaster,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        scope: form.scope,
        startDate: form.startDate,
        endDate: form.endDate,
        paymentDeadline: form.paymentDeadline || undefined,
        stakeholders: form.stakeholders || undefined,
        objectOfAgreement: form.objectOfAgreement || undefined,
      };

      // Admin submitting on behalf — add entity and counterparty fields
      if (isMaster) {
        payload.entityCacheId = form.entityCacheId;
        payload.counterpartyType = counterpartyType;
        if (counterpartyType === "VENDOR") payload.vendorId = counterpartyId;
        if (counterpartyType === "PARTNER") payload.partnerId = counterpartyId;
      }

      const agreement = await submitAgreement(payload);

      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadAgreementFile(agreement.id, fd);
      }
      return agreement;
    },
    onSuccess: (agreement) => navigate(`/agreements/${agreement.id}`),
    onError: (err) => setError(err),
  });

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (isMaster && !counterpartyId) {
      setError({
        message:
          "Please select a vendor or partner to submit this agreement on behalf of.",
      });
      return;
    }
    submit.mutate();
  }

  if (loadingEntities) return <Spinner />;

  return (
    <div>
      <Link
        to="/agreements"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Back to agreements
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Agreement</h1>
        <p className="text-sm text-gray-500 mt-1">
          The Legal team will review your submission.
        </p>
      </div>

      <div className="card p-6 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Agreement Title" required>
            <input
              className="input"
              value={form.title}
              onChange={set("title")}
              placeholder="e.g. Perjanjian Kerjasama Produksi Video"
              required
            />
          </FormField>

          {/* Entity — admin only, vendor/partner skip this */}
          {isMaster && (
            <FormField label="Rhaya Entity (PT)" required>
              <select
                className="input"
                value={form.entityCacheId}
                onChange={set("entityCacheId")}
                required
              >
                <option value="">Select PT...</option>
                {entities?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          {/* Admin: select which vendor or partner this agreement is for */}
          {isMaster && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Submitting on behalf of
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Counterparty Type" required>
                  <select
                    className="input"
                    value={counterpartyType}
                    onChange={(e) => {
                      setCounterpartyType(e.target.value);
                      setCounterpartyId("");
                    }}
                  >
                    <option value="VENDOR">Vendor</option>
                    <option value="PARTNER">Partner</option>
                  </select>
                </FormField>
                <FormField
                  label={counterpartyType === "VENDOR" ? "Vendor" : "Partner"}
                  required
                >
                  <select
                    className="input"
                    value={counterpartyId}
                    onChange={(e) => setCounterpartyId(e.target.value)}
                    required
                  >
                    <option value="">
                      Select{" "}
                      {counterpartyType === "VENDOR" ? "vendor" : "partner"}...
                    </option>
                    {counterpartyType === "VENDOR"
                      ? vendors?.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.companyName}
                          </option>
                        ))
                      : partners?.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.companyName}
                          </option>
                        ))}
                  </select>
                </FormField>
              </div>
            </div>
          )}

          <FormField label="Scope / Ruang Lingkup" required>
            <textarea
              className="input"
              rows={3}
              value={form.scope}
              onChange={set("scope")}
              placeholder="Describe the scope of this agreement..."
              required
            />
          </FormField>

          <FormField label="Object of Agreement / Objek Perjanjian">
            <textarea
              className="input"
              rows={2}
              value={form.objectOfAgreement}
              onChange={set("objectOfAgreement")}
            />
          </FormField>

          <FormField label="Stakeholders">
            <input
              className="input"
              value={form.stakeholders}
              onChange={set("stakeholders")}
              placeholder="e.g. Budi Santoso, Siti Rahayu"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Date" required>
              <input
                className="input"
                type="date"
                value={form.startDate}
                onChange={set("startDate")}
                required
              />
            </FormField>
            <FormField label="End Date" required>
              <input
                className="input"
                type="date"
                value={form.endDate}
                onChange={set("endDate")}
                required
                min={form.startDate}
              />
            </FormField>
          </div>

          <FormField label="Payment Deadline">
            <input
              className="input"
              type="date"
              value={form.paymentDeadline}
              onChange={set("paymentDeadline")}
            />
          </FormField>

          {/* File upload */}
          <FormField label="Agreement Document (PDF)">
            <label
              className={`flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                file
                  ? "border-brand-500 bg-brand-50"
                  : "border-gray-300 hover:border-brand-400"
              }`}
            >
              <Upload
                size={18}
                className={file ? "text-brand-500" : "text-gray-400"}
              />
              <div>
                {file ? (
                  <p className="text-sm font-medium text-brand-700">
                    {file.name}
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Click to upload agreement PDF
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      PDF only · max 10MB
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="mt-1 text-xs text-red-500 hover:underline"
              >
                Remove file
              </button>
            )}
          </FormField>

          {error && <ErrorMessage error={error} />}

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button
              type="submit"
              disabled={submit.isPending}
              className="btn-primary"
            >
              {submit.isPending ? "Submitting..." : "Submit Agreement"}
            </button>
            <Link to="/agreements" className="btn-secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
